import React, { useState, useRef, useEffect } from "react";
import {
  ModalOverlay,
  ModalContent,
  ModalTitle,
  CloseButton,
  FilterSection,
  FilterRow,
  FilterButton,
  FilterItem,
  DateInput,
  ApplyButton,
  CancelButton,
  ButtonsContainer,
  SourceDropdown,
  CampaignOption,
  MonthDropdown,
} from "./index.styled";
import closeIcon from "../../../assets/icons/close.webp";
import filterIcon from "../../../assets/icons/filter-icon.png";
import arrowDownIcon from "../../../assets/icons/arrow-down.webp";
import dayIcon from "../../../assets/icons/day.webp";
import weekIcon from "../../../assets/icons/week.webp";
import yearIcon from "../../../assets/icons/year.webp";

export type FilterType =
  | "all"
  | "today"
  | "this_week"
  | "month"
  | "source"
  | "dateRange";

export interface FilterState {
  dateFilter?: FilterType;
  sourceKey?: string;
  startDate?: string;
  endDate?: string;
  selectedMonth?: number;
  searchQuery?: string;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filterState: FilterState) => void;
  currentFilterState: FilterState;
  sources?: Array<{
    key: string;
    label: string;
    color: string;
    triggerId?: number;
  }>;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  currentFilterState,
  sources = [],
}) => {
  const [localFilterState, setLocalFilterState] =
    useState<FilterState>(currentFilterState);
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const sourceDropdownRef = useRef<HTMLDivElement>(null);
  const monthDropdownRef = useRef<HTMLDivElement>(null);

  const monthNames = [
    "ינואר",
    "פברואר",
    "מרץ",
    "אפריל",
    "מאי",
    "יוני",
    "יולי",
    "אוגוסט",
    "ספטמבר",
    "אוקטובר",
    "נובמבר",
    "דצמבר",
  ];

  const monthOptions = monthNames
    .map((monthName, index) => ({
      value: index + 1,
      label: monthName,
      monthIndex: index,
    }))
    .filter((month) => month.monthIndex <= new Date().getMonth())
    .map(({ value, label }) => ({ value, label }));

  // Update local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalFilterState(currentFilterState);
    }
  }, [isOpen, currentFilterState]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sourceDropdownRef.current &&
        !sourceDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSourceDropdownOpen(false);
      }
      if (
        monthDropdownRef.current &&
        !monthDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMonthDropdownOpen(false);
      }
    };

    if (isSourceDropdownOpen || isMonthDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSourceDropdownOpen, isMonthDropdownOpen]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleFilterClick = (filterType: FilterType) => {
    if (filterType === "source") {
      setIsSourceDropdownOpen(!isSourceDropdownOpen);
      setIsMonthDropdownOpen(false); // Close other dropdowns
      return;
    }
    if (filterType === "month") {
      setIsMonthDropdownOpen(!isMonthDropdownOpen);
      setIsSourceDropdownOpen(false); // Close other dropdowns
      return;
    }

    // Close all dropdowns when selecting other filters
    setIsSourceDropdownOpen(false);
    setIsMonthDropdownOpen(false);

    setLocalFilterState({
      ...localFilterState,
      dateFilter: filterType,
      ...(filterType === "all" ||
      filterType === "today" ||
      filterType === "this_week"
        ? {
            startDate: "",
            endDate: "",
            selectedMonth: undefined,
            sourceKey: undefined,
          }
        : {}),
    });
  };

  const handleMonthSelect = (month: number) => {
    setLocalFilterState({
      ...localFilterState,
      dateFilter: "month",
      selectedMonth: month,
      startDate: "",
      endDate: "",
      sourceKey: undefined,
    });
    setIsMonthDropdownOpen(false);
  };

  const handleSourceSelect = (sourceKey: string) => {
    setLocalFilterState({
      ...localFilterState,
      sourceKey,
      startDate: "",
      endDate: "",
      selectedMonth: undefined,
    });
    setIsSourceDropdownOpen(false);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalFilterState({
      ...localFilterState,
      dateFilter: "dateRange",
      startDate: value,
      selectedMonth: undefined,
      sourceKey: undefined,
    });
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalFilterState({
      ...localFilterState,
      dateFilter: "dateRange",
      endDate: value,
      selectedMonth: undefined,
      sourceKey: undefined,
    });
  };

  const handleApply = () => {
    onApply(localFilterState);
    onClose();
  };

  const handleCancel = () => {
    setLocalFilterState(currentFilterState);
    onClose();
  };

  const selectedSource = sources.find(
    (source) => source.key === localFilterState.sourceKey
  );

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <img src={closeIcon} alt="close" />
        </CloseButton>

        <ModalTitle>סנן אנשי קשר</ModalTitle>

        <FilterSection>
          <FilterRow>
            <FilterItem>
              <FilterButton
                className={
                  localFilterState.dateFilter === "all" ? "active" : ""
                }
                onClick={() => handleFilterClick("all")}
              >
                הכל
              </FilterButton>
            </FilterItem>
            <FilterItem>
              <div
                className="campaign-filter-container"
                ref={sourceDropdownRef}
                style={{ flexDirection: "row", justifyContent: "flex-end" }}
              >
                <FilterButton
                  className={localFilterState.sourceKey ? "active" : ""}
                  onClick={() => handleFilterClick("source")}
                >
                  <img src={filterIcon} alt="filter" />
                  {selectedSource ? selectedSource.label : "סנן לפי קמפיין"}
                  {selectedSource ? (
                    <img src={closeIcon} alt="clear" />
                  ) : (
                    <img
                      src={arrowDownIcon}
                      alt="dropdown"
                      className="dropdown-icon"
                      style={{
                        transform: isSourceDropdownOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  )}
                </FilterButton>
                {isSourceDropdownOpen && (
                  <SourceDropdown>
                    {localFilterState.sourceKey && (
                      <CampaignOption
                        onClick={() => {
                          setLocalFilterState({
                            ...localFilterState,
                            sourceKey: undefined,
                          });
                          setIsSourceDropdownOpen(false);
                        }}
                        className="clear-option"
                      >
                        נקה בחירה
                      </CampaignOption>
                    )}
                    {sources.map((source) => (
                      <CampaignOption
                        key={source.key}
                        onClick={() => handleSourceSelect(source.key)}
                        className={
                          localFilterState.sourceKey === source.key
                            ? "selected"
                            : ""
                        }
                      >
                        {source.label}
                      </CampaignOption>
                    ))}
                  </SourceDropdown>
                )}
              </div>
            </FilterItem>
          </FilterRow>

          <FilterRow>
            <FilterItem>
              <FilterButton
                className={
                  localFilterState.dateFilter === "today" ? "active" : ""
                }
                onClick={() => handleFilterClick("today")}
              >
                <img src={dayIcon} alt="day" />
                היום
              </FilterButton>
            </FilterItem>
            <FilterItem>
              <FilterButton
                className={
                  localFilterState.dateFilter === "this_week" ? "active" : ""
                }
                onClick={() => handleFilterClick("this_week")}
              >
                <img src={weekIcon} alt="week" />
                השבוע
              </FilterButton>
            </FilterItem>
            <FilterItem>
              <div className="month-filter-container" ref={monthDropdownRef}>
                <FilterButton
                  className={
                    localFilterState.dateFilter === "month" ? "active" : ""
                  }
                  onClick={() => handleFilterClick("month")}
                >
                  <img src={yearIcon} alt="month" />
                  {localFilterState.selectedMonth
                    ? monthNames[localFilterState.selectedMonth - 1]
                    : "בחר חודש"}
                  <img
                    src={arrowDownIcon}
                    alt="dropdown"
                    className="dropdown-icon"
                    style={{
                      transform: isMonthDropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </FilterButton>
                {isMonthDropdownOpen && (
                  <MonthDropdown>
                    {monthOptions.map((month) => (
                      <CampaignOption
                        key={month.value}
                        onClick={() => handleMonthSelect(month.value)}
                        className={
                          localFilterState.selectedMonth === month.value
                            ? "selected"
                            : ""
                        }
                      >
                        {month.label}
                      </CampaignOption>
                    ))}
                  </MonthDropdown>
                )}
              </div>
            </FilterItem>
          </FilterRow>

          {/* Bottom Row - Date Range */}
          <FilterRow className="date-range-row">
            <FilterItem className="date-range-item">
              <div className="date-range-container">
                <FilterButton
                  className={`date-range-button ${
                    localFilterState.dateFilter === "dateRange" &&
                    localFilterState.startDate
                      ? "active"
                      : ""
                  }`}
                  onClick={() => {
                    const startDateInput = document.querySelector(
                      'input[type="date"][data-date-type="start"]'
                    ) as HTMLInputElement;
                    startDateInput?.click();
                  }}
                >
                  <img src={yearIcon} alt="date" />
                  {localFilterState.startDate
                    ? new Date(localFilterState.startDate).toLocaleDateString(
                        "he-IL"
                      )
                    : "מתאריך"}
                </FilterButton>
                <DateInput
                  type="date"
                  value={localFilterState.startDate || ""}
                  onChange={handleStartDateChange}
                  data-date-type="start"
                  className="date-input"
                />
              </div>
            </FilterItem>
            <FilterItem className="date-range-item">
              <div className="date-range-container">
                <FilterButton
                  className={`date-range-button ${
                    localFilterState.dateFilter === "dateRange" &&
                    localFilterState.endDate
                      ? "active"
                      : ""
                  }`}
                  onClick={() => {
                    const endDateInput = document.querySelector(
                      'input[type="date"][data-date-type="end"]'
                    ) as HTMLInputElement;
                    endDateInput?.click();
                  }}
                >
                  <img src={yearIcon} alt="date" />
                  {localFilterState.endDate
                    ? new Date(localFilterState.endDate).toLocaleDateString(
                        "he-IL"
                      )
                    : "עד תאריך"}
                </FilterButton>
                <DateInput
                  type="date"
                  value={localFilterState.endDate || ""}
                  onChange={handleEndDateChange}
                  data-date-type="end"
                  className="date-input"
                />
              </div>
            </FilterItem>
          </FilterRow>
        </FilterSection>

        <ButtonsContainer>
          <ApplyButton onClick={handleApply}>החל</ApplyButton>
          <CancelButton onClick={handleCancel}>ביטול</CancelButton>
        </ButtonsContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default FilterModal;
